import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import type {
  AppointmentTypeStatus,
  CalendarEventStatusDefinition,
  CreateCalendarEventStatusDefinitionInput,
  UpdateCalendarEventStatusDefinitionInput,
} from '../types/Calendar';
import {
  createEventStatus,
  listEventStatuses,
  updateEventStatus,
} from '../services/calendarService';
import styles from './CalendarEventStatusesPage.module.css';

type StatusEditorState = {
  name: string;
  code: string;
  color: string;
  sortOrder: string;
  status: AppointmentTypeStatus;
  isDefault: boolean;
  countsAsBooking: boolean;
  suppressesReminders: boolean;
};

const emptyEditorState = (): StatusEditorState => ({
  name: '',
  code: '',
  color: '#64748b',
  sortOrder: '0',
  status: 'active',
  isDefault: false,
  countsAsBooking: true,
  suppressesReminders: false,
});

const toEditorState = (item: CalendarEventStatusDefinition): StatusEditorState => ({
  name: item.name,
  code: item.code,
  color: item.color || '#64748b',
  sortOrder: String(item.sortOrder),
  status: item.status,
  isDefault: item.isDefault,
  countsAsBooking: item.countsAsBooking,
  suppressesReminders: item.suppressesReminders,
});

export const CalendarEventStatusesPage: React.FC = () => {
  const { t } = useTranslation(['calendar', 'common']);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [statuses, setStatuses] = React.useState<CalendarEventStatusDefinition[]>([]);
  const [editorState, setEditorState] = React.useState<StatusEditorState>(() => emptyEditorState());
  const [selectedStatusId, setSelectedStatusId] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | AppointmentTypeStatus>('all');
  const [error, setError] = React.useState('');
  const [statusMessage, setStatusMessage] = React.useState('');

  const selectedStatus = React.useMemo(
    () => statuses.find((item) => item.id === selectedStatusId) || null,
    [selectedStatusId, statuses],
  );

  const loadStatuses = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const loaded = await listEventStatuses();
      setStatuses(loaded);
    } catch (loadError) {
      setError(loadError instanceof Error
        ? loadError.message
        : t('eventStatusesLoadFailed', { defaultValue: 'Failed to load event statuses.' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadStatuses();
  }, [loadStatuses]);

  const filteredStatuses = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return statuses.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query);
    });
  }, [searchTerm, statusFilter, statuses]);

  const activeCount = React.useMemo(
    () => statuses.filter((item) => item.status === 'active').length,
    [statuses],
  );

  const updateField = <K extends keyof StatusEditorState>(key: K, value: StatusEditorState[K]) => {
    setEditorState((previous) => ({ ...previous, [key]: value }));
  };

  const resetEditor = () => {
    setSelectedStatusId(null);
    setEditorState(emptyEditorState());
    setStatusMessage('');
    setError('');
  };

  const handleSelect = (item: CalendarEventStatusDefinition) => {
    setSelectedStatusId(item.id);
    setEditorState(toEditorState(item));
    setStatusMessage('');
    setError('');
  };

  const handleSave = async () => {
    const name = editorState.name.trim();
    if (!name) {
      setError(t('eventStatusNameRequired', { defaultValue: 'Name is required.' }));
      return;
    }

    const sortOrder = Number(editorState.sortOrder);
    if (!Number.isFinite(sortOrder)) {
      setError(t('eventStatusSortOrderInvalid', { defaultValue: 'Sort order must be a number.' }));
      return;
    }

    setSaving(true);
    setError('');
    setStatusMessage('');

    try {
      if (selectedStatusId) {
        const payload: UpdateCalendarEventStatusDefinitionInput = {
          name,
          color: editorState.color.trim() || undefined,
          sortOrder,
          isDefault: editorState.isDefault,
          countsAsBooking: editorState.countsAsBooking,
          suppressesReminders: editorState.suppressesReminders,
          status: editorState.status,
        };
        await updateEventStatus(selectedStatusId, payload);
        setStatusMessage(t('eventStatusUpdated', { defaultValue: 'Event status updated.' }));
      } else {
        const payload: CreateCalendarEventStatusDefinitionInput = {
          name,
          code: editorState.code.trim() || undefined,
          color: editorState.color.trim() || undefined,
          sortOrder,
          isDefault: editorState.isDefault,
          countsAsBooking: editorState.countsAsBooking,
          suppressesReminders: editorState.suppressesReminders,
          status: editorState.status,
        };
        await createEventStatus(payload);
        setStatusMessage(t('eventStatusCreated', { defaultValue: 'Event status created.' }));
        resetEditor();
      }

      await loadStatuses();
    } catch (saveError) {
      setError(saveError instanceof Error
        ? saveError.message
        : t('eventStatusSaveFailed', { defaultValue: 'Failed to save event status.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveToggle = async (item: CalendarEventStatusDefinition) => {
    setSaving(true);
    setError('');
    setStatusMessage('');

    try {
      const nextStatus: AppointmentTypeStatus = item.status === 'active' ? 'archived' : 'active';
      await updateEventStatus(item.id, { status: nextStatus });
      setStatusMessage(nextStatus === 'archived'
        ? t('eventStatusArchived', { defaultValue: 'Event status archived.' })
        : t('eventStatusRestored', { defaultValue: 'Event status restored.' }));
      await loadStatuses();
      if (selectedStatusId === item.id) {
        setEditorState((previous) => ({ ...previous, status: nextStatus }));
      }
    } catch (archiveError) {
      setError(archiveError instanceof Error
        ? archiveError.message
        : t('eventStatusSaveFailed', { defaultValue: 'Failed to save event status.' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('calendar'), path: '/calendar' },
          { label: t('manageEventStatuses', { defaultValue: 'Event statuses' }), path: '/calendar/event-statuses' },
        ]}
      />

      <section className={styles.hero}>
        <div>
          <h1 className={styles.title}>{t('manageEventStatuses', { defaultValue: 'Event statuses' })}</h1>
          <p className={styles.subtitle}>
            {t('manageEventStatusesSubtitle', {
              defaultValue: 'Add custom calendar event statuses or archive ones you no longer use.',
            })}
          </p>
          <div className={styles.heroStats}>
            <span>{t('totalCount', { defaultValue: '{{count}} total', count: statuses.length })}</span>
            <span>{t('activeCount', { defaultValue: '{{count}} active', count: activeCount })}</span>
          </div>
        </div>
        <div className={styles.heroActions}>
          <Link className="btn btn-secondary" to="/calendar">
            {t('backToCalendar', { defaultValue: 'Back to calendar' })}
          </Link>
        </div>
      </section>

      {(error || statusMessage) ? (
        <section className={styles.messages}>
          {error ? <p className={styles.error}>{error}</p> : null}
          {statusMessage ? <p className={styles.success}>{statusMessage}</p> : null}
        </section>
      ) : null}

      {loading ? (
        <Spinner />
      ) : (
        <section className={styles.layout}>
          <aside className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>
                {selectedStatusId
                  ? t('editEventStatus', { defaultValue: 'Edit event status' })
                  : t('addEventStatus', { defaultValue: 'Add event status' })}
              </h2>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('name', { ns: 'common', defaultValue: 'Name' })}</label>
                <input
                  className="text-input"
                  value={editorState.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  maxLength={200}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('code', { defaultValue: 'Code' })}</label>
                <input
                  className="text-input"
                  value={editorState.code}
                  onChange={(event) => updateField('code', event.target.value)}
                  maxLength={64}
                  disabled={Boolean(selectedStatus)}
                  placeholder={t('codeAutoGenerated', { defaultValue: 'Optional — generated from name' })}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('color', { defaultValue: 'Color' })}</label>
                <input
                  className="text-input"
                  type="color"
                  value={editorState.color || '#64748b'}
                  onChange={(event) => updateField('color', event.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('sortOrder', { defaultValue: 'Sort order' })}</label>
                <input
                  className="text-input"
                  type="number"
                  value={editorState.sortOrder}
                  onChange={(event) => updateField('sortOrder', event.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('status', { defaultValue: 'Status' })}</label>
                <select
                  className="text-input"
                  value={editorState.status}
                  onChange={(event) => updateField('status', event.target.value as AppointmentTypeStatus)}
                >
                  <option value="active">{t('active', { ns: 'common', defaultValue: 'Active' })}</option>
                  <option value="archived">{t('archived', { defaultValue: 'Archived' })}</option>
                </select>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={editorState.isDefault}
                    onChange={(event) => updateField('isDefault', event.target.checked)}
                  />
                  {' '}
                  {t('defaultStatus', { defaultValue: 'Default for new events' })}
                </label>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={editorState.countsAsBooking}
                    onChange={(event) => updateField('countsAsBooking', event.target.checked)}
                  />
                  {' '}
                  {t('countsAsBooking', { defaultValue: 'Counts as booking (blocks availability)' })}
                </label>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={editorState.suppressesReminders}
                    onChange={(event) => updateField('suppressesReminders', event.target.checked)}
                  />
                  {' '}
                  {t('suppressesReminders', { defaultValue: 'Suppresses reminders' })}
                </label>
              </div>
            </div>

            <div className={styles.footer}>
              {selectedStatusId ? (
                <button className="btn btn-secondary" type="button" onClick={resetEditor} disabled={saving}>
                  {t('cancelEdit', { defaultValue: 'Cancel edit' })}
                </button>
              ) : (
                <button className="btn btn-secondary" type="button" onClick={resetEditor} disabled={saving}>
                  {t('resetForm', { defaultValue: 'Reset form' })}
                </button>
              )}
              <button className="btn btn-primary" type="button" onClick={() => void handleSave()} disabled={saving}>
                {saving
                  ? t('saving', { ns: 'common', defaultValue: 'Saving…' })
                  : selectedStatusId
                    ? t('saveChanges', { defaultValue: 'Save changes' })
                    : t('addEventStatus', { defaultValue: 'Add event status' })}
              </button>
            </div>
          </aside>

          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>{t('existingEventStatuses', { defaultValue: 'Existing event statuses' })}</h2>
              <span className={styles.count}>{filteredStatuses.length}/{statuses.length}</span>
            </div>

            <div className={styles.filters}>
              <input
                className="text-input"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('searchStatuses', { defaultValue: 'Search statuses' })}
              />
              <select
                className="text-input"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as 'all' | AppointmentTypeStatus)}
              >
                <option value="all">{t('all', { ns: 'common', defaultValue: 'All' })}</option>
                <option value="active">{t('activeOnly', { defaultValue: 'Active only' })}</option>
                <option value="archived">{t('archivedOnly', { defaultValue: 'Archived only' })}</option>
              </select>
            </div>

            <div className={styles.list}>
              {filteredStatuses.length === 0 ? (
                <p className={styles.empty}>{t('noEventStatuses', { defaultValue: 'No event statuses found.' })}</p>
              ) : (
                filteredStatuses.map((item) => (
                  <div
                    key={item.id}
                    className={`${styles.item} ${selectedStatusId === item.id ? styles.itemActive : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelect(item);
                      }
                    }}
                  >
                    <div className={styles.itemHeader}>
                      <strong>{item.name}</strong>
                      <span className={`${styles.badge} ${item.status === 'active' ? styles.badgeActive : styles.badgeMuted}`}>
                        {item.status === 'active'
                          ? t('active', { ns: 'common', defaultValue: 'Active' })
                          : t('archived', { defaultValue: 'Archived' })}
                      </span>
                    </div>
                    <div className={styles.itemMeta}>
                      <span
                        className={styles.colorSwatch}
                        style={{ backgroundColor: item.color || '#64748b' }}
                        aria-hidden
                      />
                      <span>{item.code}</span>
                      {item.isDefault ? <span>{t('default', { defaultValue: 'Default' })}</span> : null}
                      {item.isSystem ? <span>{t('systemStatus', { defaultValue: 'System' })}</span> : null}
                    </div>
                    <div className={styles.footer}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleArchiveToggle(item);
                        }}
                        disabled={saving}
                      >
                        {item.status === 'active'
                          ? t('archive', { defaultValue: 'Archive' })
                          : t('restore', { defaultValue: 'Restore' })}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </section>
      )}
    </div>
  );
};

export default CalendarEventStatusesPage;
